import { httpResource } from '@angular/common/http';
import { computed, Injectable, Signal } from '@angular/core';
import type { EntityChangesResponse } from '@zag/core/models';

/**
 * Parameters for loading entity audit changes.
 * All three fields are required and must be non-null to trigger a request.
 */
export interface EntityAuditParams {
  schema: string;
  table: string;
  recordId: string;
}

/** Signal value type for entity audit params — null/undefined skips loading. */
type EntityAuditParamsSignal = EntityAuditParams | null | undefined;

/**
 * Service for fetching entity-specific audit log entries.
 *
 * Usage: Inject this service and call `createEntityResource()` with a signal
 * that provides the entity parameters. The returned resource reacts automatically
 * when the parameters change.
 */
@Injectable({ providedIn: 'root' })
export class AuditService {
  private readonly basePath = '/api/v1/audit';

  /**
   * Creates a reactive HTTP resource that loads audit changes for a specific entity.
   *
   * @param params A signal (or computed) returning EntityAuditParams, or null/undefined
   *               to skip loading (e.g., when in create mode or entity ID is unknown).
   */
  createEntityResource(params: Signal<EntityAuditParamsSignal>) {
    return httpResource<EntityChangesResponse>(
      computed(() => {
        const p = params();
        if (!p?.recordId) return undefined;
        return `${this.basePath}/entity?schema=${encodeURIComponent(p.schema)}&table=${encodeURIComponent(p.table)}&recordId=${encodeURIComponent(p.recordId)}&limit=50`;
      })
    );
  }
}
